import React, { useState, useEffect } from 'react';
import { Switch, Skeleton, message } from 'antd';
import { useSelector } from 'react-redux';
import { propertyService } from '@iso/services';
import { setMeasurementsEnabled } from '@iso/lib/helpers/mapMeasurements';

const SUCCESS_CODE = '0000';

/**
 * How this property's maps are drawn — one value shared by everyone who opens
 * them, which is why it lives here rather than in a browser preference.
 *
 * The same switch is on the map itself, under the Show options gear, so it can
 * be flipped while looking at the paddocks it affects. Both write the same
 * property field.
 */
const MapDisplaySettings = ({ propertyId }) => {
  const activePermission = useSelector((state) => state.permission.activePermission);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEdgeDistance, setShowEdgeDistance] = useState(false);
  // The fetch below is the only thing that tells this page what the property
  // actually holds, so a failed read must not leave the switch sitting on its
  // seed value looking like an answer.
  const [loadFailed, setLoadFailed] = useState(false);

  const canEdit = ['owner', 'admin', 'modify'].includes(activePermission);

  useEffect(() => {
    if (!propertyId) return;

    setLoading(true);
    setLoadFailed(false);
    // viewProperty resolves for any HTTP 200 and still answers in the older
    // `code` shape, so an application-level refusal — access denied and the
    // like — arrives here rather than in the catch. Throw it across so both
    // failures land in one place: without this the switch keeps its `false`
    // seed and renders "Hide" for a property that has the labels on, and the
    // next click writes that back as though someone had chosen it.
    propertyService.viewProperty(propertyId).then((res) => {
      if (res.code === SUCCESS_CODE) {
        setShowEdgeDistance(Number(res.property.map_show_edge_distance) === 1);
        return;
      }

      throw new Error(res.message || 'Request failed');
    }).catch((err) => {
      setLoadFailed(true);
      console.error('Failed to load map display settings:', err);
      message.error('Failed to load map display settings');
    }).finally(() => setLoading(false));
  }, [propertyId]);

  const handleChange = (value) => {
    setSaving(true);
    setShowEdgeDistance(value);

    // `{ success, message }`, unlike viewProperty above, which still answers in
    // the older `code` shape — this endpoint is new enough to use the one the
    // standards ask for.
    propertyService.updateMapSettings(propertyId, value).then((res) => {
      if (res.success) {
        // Any map already open in this session picks the change up without a
        // reload; every other session reads it when it next loads the property.
        setMeasurementsEnabled(value);
        message.success(res.message || 'Map display settings updated');
        return;
      }

      setShowEdgeDistance(!value);
      message.error(res.message || 'Failed to update map display settings');
    }).catch((err) => {
      setShowEdgeDistance(!value);
      console.error('Failed to update map display settings:', err);
      message.error('Failed to update map display settings');
    }).finally(() => setSaving(false));
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 2 }} />;
  }

  return (
    <div>
      <p style={{ marginBottom: 16, color: '#666' }}>
        Controls how shapes are labelled on every map of this property — the property map, the shared public map and the maps inside reports. The setting applies to everyone who opens them.
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, maxWidth: 720 }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Distance and area on shapes</div>
          <div style={{ color: '#666' }}>
            Show the length of every edge of a paddock, boundary or drawn line, and the area it encloses. Turning this off clears both, leaving the shapes unlabelled.
          </div>
        </div>

        <Switch
          style={{ width: 100 }}
          checkedChildren="Show"
          unCheckedChildren="Hide"
          checked={showEdgeDistance}
          disabled={!canEdit || saving || loadFailed}
          loading={saving}
          onChange={handleChange}
        />
      </div>

      {loadFailed && (
        <p style={{ marginTop: 16, color: '#cf1322' }}>
          Couldn't read this property's current setting, so the switch above isn't showing it. Reload the page to try again.
        </p>
      )}

      {!canEdit && !loadFailed && (
        <p style={{ marginTop: 16, color: '#999' }}>
          Only owners, admins and modifiers can change this.
        </p>
      )}
    </div>
  );
};

export default MapDisplaySettings;
